/**
 * <s-closing>: the homepage's last word ("Buy").
 * - Tailors the headline, line and main button to the tier the visitor picked in "Three ways to launch"
 *   (a real pick or the quiz result, shared through sessionStorage "the4-tier" and the "the4:tier" event).
 *   Elements carry data-v="default|essential|premium|custom"; only the matching set is shown.
 * - Sets [data-sunup] once the card is in view (the sun rises behind the card; CSS handles motion).
 * - Sets [data-next] while the main button is hovered or focused (a coral dot lands on the mini globe).
 */
class SClosing extends HTMLElement {
  #observer = null;

  #onTier = (event) => this.#apply(event.detail);

  #onEnter = (event) => {
    const cta = event.target.closest?.('[data-close-cta]');
    if (!cta) return;
    if (!this.hasAttribute('data-next') && event.type === 'pointerover') this.#launch(event.clientX);
    this.setAttribute('data-next', '');
  };

  #onLeave = (event) => {
    const cta = event.target.closest?.('[data-close-cta]');
    if (cta && !cta.contains(event.relatedTarget)) this.removeAttribute('data-next');
    if (cta && !cta.contains(event.relatedTarget)) {
      const magnet = cta.querySelector('[data-magnet]') || cta;
      magnet.style.removeProperty('translate');
    }
  };

  #lastLaunch = 0;
  #landTimer = 0;

  /** Planet close: the visitor's own arc flies from under the pointer into the studio. */
  #launch(clientX) {
    const globe = this.querySelector('s-proof-globe');
    const now = performance.now();
    if (!globe?.launchFrom || now - this.#lastLaunch < 1400) return;
    this.#lastLaunch = now;
    globe.launchFrom(clientX);
  }

  #onLanded = () => {
    this.setAttribute('data-landed', '');
    clearTimeout(this.#landTimer);
    this.#landTimer = setTimeout(() => this.removeAttribute('data-landed'), 2600);
  };

  /** Main button leans toward a fine pointer (max ~6px), motion allowed only. */
  #onMove = (event) => {
    if (event.pointerType !== 'mouse' || this.#reduced) return;
    const cta = event.target.closest?.('[data-close-cta]');
    if (!cta) return;
    const magnet = cta.querySelector('[data-magnet]') || cta;
    const box = cta.getBoundingClientRect();
    const dx = (event.clientX - (box.left + box.width / 2)) / box.width;
    const dy = (event.clientY - (box.top + box.height / 2)) / box.height;
    magnet.style.translate = `${(dx * 12).toFixed(1)}px ${(dy * 8).toFixed(1)}px`;
  };

  #reduced = false;

  connectedCallback() {
    this.#reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.addEventListener('pointermove', this.#onMove);
    this.addEventListener('globe:landed', this.#onLanded);
    let saved = null;
    try { saved = sessionStorage.getItem('the4-tier'); } catch {}
    this.#apply(saved);
    document.addEventListener('the4:tier', this.#onTier);
    this.addEventListener('pointerover', this.#onEnter);
    this.addEventListener('pointerout', this.#onLeave);
    this.addEventListener('focusin', this.#onEnter);
    this.addEventListener('focusout', this.#onLeave);
    if (!('IntersectionObserver' in window)) {
      this.setAttribute('data-sunup', '');
      return;
    }
    this.#observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.setAttribute('data-sunup', '');
      this.#observer?.disconnect();
    }, { threshold: 0.3 });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.removeEventListener('pointermove', this.#onMove);
    this.removeEventListener('globe:landed', this.#onLanded);
    clearTimeout(this.#landTimer);
    document.removeEventListener('the4:tier', this.#onTier);
    this.removeEventListener('pointerover', this.#onEnter);
    this.removeEventListener('pointerout', this.#onLeave);
    this.removeEventListener('focusin', this.#onEnter);
    this.removeEventListener('focusout', this.#onLeave);
    this.#observer?.disconnect();
  }

  #apply(tier) {
    const known = ['essential', 'premium', 'custom'];
    const active = known.includes(tier) ? tier : 'default';
    if (this.dataset.tier === active) return;
    this.dataset.tier = active;
    for (const el of this.querySelectorAll('[data-v]')) {
      el.hidden = el.dataset.v !== active && !(el.dataset.v === 'picked' && active !== 'default');
    }
  }
}

if (!customElements.get('s-closing')) customElements.define('s-closing', SClosing);
